import React, { useState } from 'react'
import styled from 'styled-components'

import { theme } from '../../../theme'

interface InputProps extends React.InputHTMLAttributes<HTMLInputElement> {
  label?: string
  icon?: React.ReactNode
  error?: string
  helper?: string
  showPasswordToggle?: boolean
}

const InputContainer = styled.div`
  width: 100%;
`

const Label = styled.label`
  display: block;
  margin-bottom: 0.5rem;
`

const RequiredAsterisk = styled.span`
  margin-left: 0.25rem;
`

const InputWrapper = styled.div`
  position: relative;
`

const IconWrapper = styled.div`
  position: absolute;
  top: 0;
  bottom: 0;
  left: 0;
  display: flex;
  align-items: center;
  padding-left: 1rem;
  pointer-events: none;
  color: ${theme.colors.greyText};
`

const StyledInput = styled.input<{
  hasIcon?: boolean
  hasPasswordToggle?: boolean
  hasError?: boolean
}>`
  width: 100%;
  height: 3.5rem;
  padding: 0.75rem 1rem;
  border: 1px solid
    ${({ hasError }) =>
      hasError ? theme.colors.error : theme.colors.borderColor};
  border-radius: ${theme.radius.default};
  background-color: ${theme.colors.secondaryDefault};
  color: ${theme.colors.greyText};
  padding-left: ${({ hasIcon }) => (hasIcon ? '3rem' : '1rem')};
  padding-right: ${({ hasPasswordToggle }) =>
    hasPasswordToggle ? '3rem' : '1rem'};
  transition: all 0.2s;

  &:focus {
    outline: none;
    border-color: ${({ hasError }) =>
      hasError ? theme.colors.error : theme.colors.altovita};
    box-shadow: 0 0 0 2px
      ${({ hasError }) =>
        hasError ? `${theme.colors.error}33` : `${theme.colors.altovita}33`};
  }
`

const PasswordToggleButton = styled.button`
  position: absolute;
  right: 1rem;
  top: 50%;
  transform: translateY(-50%);
  color: ${theme.colors.greyText};

  &:hover {
    color: ${theme.colors.altovita};
  }

  &:focus {
    outline: none;
  }
`

const ErrorText = styled.p`
  margin-top: 0.25rem;
  font-size: 0.875rem;
  color: ${theme.colors.error};
`

const HelperText = styled.p`
  margin-top: 0.25rem;
  font-size: 0.875rem;
  color: ${theme.colors.greyText};
`

export const Input = ({
  label,
  icon,
  error,
  helper,
  showPasswordToggle = false,
  type = 'text',
  className = '',
  ...props
}: InputProps) => {
  const [showPassword, setShowPassword] = useState(false)
  const [inputType, setInputType] = useState(type)

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword)
    setInputType(showPassword ? 'password' : 'text')
  }

  return (
    <InputContainer className={className}>
      {label && (
        <Label>
          {label}
          {props.required && <RequiredAsterisk>*</RequiredAsterisk>}
        </Label>
      )}

      <InputWrapper>
        {icon && <IconWrapper>{icon}</IconWrapper>}

        <StyledInput
          type={showPasswordToggle ? inputType : type}
          hasIcon={!!icon}
          hasPasswordToggle={showPasswordToggle}
          hasError={!!error}
          {...props}
        />

        {showPasswordToggle && type === 'password' && (
          <PasswordToggleButton
            type="button"
            onClick={togglePasswordVisibility}
          >
            <i
              className={
                showPassword ? 'fas fa-eye text-lg' : 'fas fa-eye-slash text-lg'
              }
            ></i>
          </PasswordToggleButton>
        )}
      </InputWrapper>

      {error && <ErrorText>{error}</ErrorText>}

      {helper && !error && <HelperText>{helper}</HelperText>}
    </InputContainer>
  )
}
